import styled from 'styled-components';
import { KakaoDraft, renderKakaoDraftText } from '../../utils/kakaoTemplate';

export const PreviewDisclaimer = styled.p`
  margin: 12px 0 24px; color: ${({ theme }) => theme.color.text_color_200};
  font-size: 14px; font-weight: 600; line-height: 1.5;
`;

const Stage = styled.section`
  min-width: 0; min-height: 380px; padding: 22px 18px 28px; border-radius: 10px;
  background: ${({ theme }) => theme.name === 'dark' ? '#344453' : '#BFD0DE'};
  color: ${({ theme }) => theme.name === 'dark' ? '#E7EDF2' : '#464D54'};
`;
const DateLine = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 24px;
  color: ${({ theme }) => theme.name === 'dark' ? '#D2DAE1' : '#4E5B66'};
  font-size: 12px; white-space: nowrap;
  &::before, &::after { content: ''; height: 1px; flex: 1; background: ${({ theme }) => theme.name === 'dark' ? '#6F8190' : '#A3B8C8'}; }
`;
const Thread = styled.div`display: grid; grid-template-columns: 36px minmax(0, 1fr); gap: 10px;`;
const Avatar = styled.div`
  width: 36px; height: 36px; border-radius: 13px;
  background: ${({ theme }) => theme.name === 'dark' ? '#E8EDF0' : '#FFFFFF'};
`;
const ChannelName = styled.div`margin: 1px 0 8px; font-size: 12px; font-weight: 600; overflow-wrap: anywhere;`;
const Card = styled.div`
  position: relative; max-width: 370px; border-radius: 8px;
  background: ${({ theme }) => theme.name === 'dark' ? '#282C32' : '#FFFFFF'};
  color: ${({ theme }) => theme.name === 'dark' ? '#F0F2F4' : '#3E4146'};
  overflow: visible;
`;
const CardHeader = styled.div`
  padding: 10px 13px; border-radius: 8px 8px 0 0; background: ${({ theme }) => theme.name === 'dark' ? '#F2DC19' : '#FEE500'};
  color: #303238; font-size: 13px; font-weight: 700;
`;
const KakaoMark = styled.span`
  position: absolute; top: -12px; right: -12px; width: 36px; height: 36px;
  display: grid; place-items: center; border-radius: 50%; background: #3F4145; color: #FFFFFF;
  font-size: 10px; font-weight: 700;
`;
const Content = styled.div`padding: 14px 13px; font-size: 13px; line-height: 1.6; white-space: pre-wrap; overflow-wrap: anywhere;`;
const Image = styled.img`display: block; width: 100%; max-height: 230px; object-fit: contain; background: #ECEFF2;`;
const ImagePlaceholder = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
  min-height: 150px; padding: 12px; background: ${({ theme }) => theme.name === 'dark' ? '#3D444D' : '#F3F5F7'};
  color: ${({ theme }) => theme.name === 'dark' ? '#C6CED6' : '#69727D'}; text-align: center;
  font-size: 11px; line-height: 1.5;
  span { font-size: 26px; line-height: 1; }
`;
const Emphasis = styled.div`
  margin: 0 13px; padding: 16px 0 13px; text-align: left; white-space: pre-wrap; overflow-wrap: anywhere;
  border-bottom: 1px solid ${({ theme }) => theme.name === 'dark' ? '#4C525B' : '#DEE2E6'};
  span { display: block; color: ${({ theme }) => theme.name === 'dark' ? '#C3C8CF' : '#777D85'}; font-size: 12px; }
  strong { display: block; margin-top: 4px; font-size: 18px; line-height: 1.4; }
`;
const ItemHeader = styled.div`
  margin: 0 13px; padding: 14px 0 11px; font-size: 14px; font-weight: 700; white-space: pre-wrap;
  border-bottom: 1px solid ${({ theme }) => theme.name === 'dark' ? '#4C525B' : '#DEE2E6'};
`;
const Highlight = styled.div`
  display: flex; flex-direction: row-reverse; justify-content: space-between; align-items: center; gap: 9px; margin: 0 13px; padding: 11px 0;
  border-bottom: 1px solid ${({ theme }) => theme.name === 'dark' ? '#444B54' : '#E6E8EA'};
  img { width: 42px; height: 42px; object-fit: cover; border-radius: 4px; }
  strong { display: block; font-size: 13px; } span { display: block; margin-top: 2px; font-size: 11px; }
`;
const ItemRows = styled.div`
  margin: 0 13px; border-bottom: 1px solid ${({ theme }) => theme.name === 'dark' ? '#444B54' : '#E6E8EA'};
  div { display: flex; justify-content: space-between; gap: 10px; padding: 7px 0; font-size: 12px; }
  div + div { border-top: 1px solid ${({ theme }) => theme.name === 'dark' ? '#444B54' : '#E6E8EA'}; }
  .summary { font-weight: 700; }
  strong { text-align: right; overflow-wrap: anywhere; }
`;
const Supplement = styled.div`
  padding: 0 13px 12px; font-size: 11px; line-height: 1.5; white-space: pre-wrap; overflow-wrap: anywhere;
`;
const Actions = styled.div`
  padding: 0 13px 13px; display: flex; flex-direction: column; gap: 6px;
  span { display: block; padding: 8px; border: 1px solid ${({ theme }) => theme.name === 'dark' ? '#535A62' : '#E2E5E9'};
    border-radius: 5px; text-align: center; font-size: 12px; font-weight: 600; }
`;
const QuickReplies = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;
  span { padding: 6px 9px; border-radius: 20px; background: ${({ theme }) => theme.name === 'dark' ? '#536679' : '#E6EFF5'}; font-size: 11px; }
`;

export default function KakaoTemplatePreview({ draft, variables, imageUrl, highlightImageUrl, channelLabel, showExamples = false }: {
  draft: KakaoDraft; variables: Record<string, string>; imageUrl?: string; highlightImageUrl?: string;
  channelLabel?: string; showExamples?: boolean;
}) {
  const showImage = (draft.emphasizeType === 'IMAGE' || draft.emphasizeType === 'ITEM_LIST') && imageUrl;
  const showHighlight = draft.emphasizeType === 'ITEM_LIST' && (draft.highlightTitle || draft.highlightDescription || highlightImageUrl || showExamples);
  return <Stage aria-label="알림톡 미리보기">
    <DateLine>예시 미리보기</DateLine>
    <Thread>
      <Avatar aria-hidden="true" />
      <div>
        <ChannelName>{channelLabel || '카카오톡 채널'}</ChannelName>
        <Card>
          <CardHeader>알림톡 도착</CardHeader><KakaoMark aria-hidden="true">kakao</KakaoMark>
          {showImage && <Image src={imageUrl} alt="템플릿 이미지" />}
          {!showImage && showExamples && (draft.emphasizeType === 'IMAGE' || draft.emphasizeType === 'ITEM_LIST') && <ImagePlaceholder><span aria-hidden="true">▧</span>이미지를 선택하면 여기에 표시됩니다.</ImagePlaceholder>}
          {draft.emphasizeType === 'TEXT' && (draft.emphasizeTitle || draft.emphasizeSubtitle || showExamples) && <Emphasis>
            <span>{draft.emphasizeSubtitle ? renderKakaoDraftText(draft.emphasizeSubtitle, variables) : showExamples ? '강조 보조 문구 예시' : null}</span>
            <strong>{draft.emphasizeTitle ? renderKakaoDraftText(draft.emphasizeTitle, variables) : showExamples ? '강조 제목 예시' : null}</strong>
          </Emphasis>}
          {draft.emphasizeType === 'ITEM_LIST' && <>
            {(draft.header || showExamples) && <ItemHeader>{draft.header ? renderKakaoDraftText(draft.header, variables) : '리스트 제목 예시'}</ItemHeader>}
            {showHighlight && <Highlight>
              {highlightImageUrl && <img src={highlightImageUrl} alt="하이라이트 이미지" />}
              <div><strong>{draft.highlightTitle ? renderKakaoDraftText(draft.highlightTitle, variables) : showExamples ? '하이라이트 제목' : null}</strong><span>{draft.highlightDescription ? renderKakaoDraftText(draft.highlightDescription, variables) : showExamples ? '하이라이트 설명' : null}</span></div>
            </Highlight>}
            {(draft.items.length > 0 || draft.summaryTitle || showExamples) && <ItemRows>
              {draft.items.length ? draft.items.map((item, index) => <div key={index}><span>{item.title}</span><strong>{renderKakaoDraftText(item.description, variables)}</strong></div>) : showExamples && <div><span>항목 이름</span><strong>항목 내용 예시</strong></div>}
              {(draft.summaryTitle || showExamples) && <div className="summary"><span>{draft.summaryTitle || '합계 예시'}</span><strong>{draft.summaryDescription ? renderKakaoDraftText(draft.summaryDescription, variables) : showExamples ? '0원' : ''}</strong></div>}
            </ItemRows>}
          </>}
          <Content>{draft.content ? renderKakaoDraftText(draft.content, variables) : '알림톡 내용을 입력해 주세요.'}</Content>
          {(draft.messageType === 'EX' || draft.messageType === 'MI') && draft.extra && <Supplement>{draft.extra}</Supplement>}
          {(draft.messageType === 'AD' || draft.messageType === 'MI') && draft.ad && <Supplement>{draft.ad}</Supplement>}
          {draft.buttons.length > 0 && <Actions>{draft.buttons.map((button, index) => <span key={index}>{button.buttonName || '버튼 이름'}</span>)}</Actions>}
        </Card>
        {draft.quickReplies.length > 0 && <QuickReplies>{draft.quickReplies.map((reply, index) => <span key={index}>{reply.name || '바로연결'}</span>)}</QuickReplies>}
      </div>
    </Thread>
  </Stage>;
}
